import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { INTEGRATIONS, INTEGRATION_CATEGORIES } from './registry';
import api from './../../api/axios';
import IntegrationActivityPanel from './IntegrationActivityPanel';
import IntegrationHealthPanel, { scoreFor } from './IntegrationHealthPanel';
import IntegrationManagementPanel from './IntegrationManagementPanel';

const STATUS_META = {
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: 'fa-circle-check' },
  ready: { label: 'Operational', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: 'fa-circle-check' },
  configured: { label: 'Configured', className: 'bg-blue-50 text-blue-700 ring-blue-200', icon: 'fa-circle-check' },
  attention: { label: 'Needs attention', className: 'bg-amber-50 text-amber-800 ring-amber-200', icon: 'fa-triangle-exclamation' },
  permission_required: { label: 'Permission needed', className: 'bg-amber-50 text-amber-800 ring-amber-200', icon: 'fa-key' },
  reconnect_required: { label: 'Reconnect', className: 'bg-red-50 text-red-700 ring-red-200', icon: 'fa-rotate' },
  inactive: { label: 'Not connected', className: 'bg-gray-100 text-gray-600 ring-gray-200', icon: 'fa-circle' },
  not_connected: { label: 'Not connected', className: 'bg-gray-100 text-gray-600 ring-gray-200', icon: 'fa-circle' },
  coming: { label: 'Coming soon', className: 'bg-gray-100 text-gray-500 ring-gray-200', icon: 'fa-clock' },
};

const StatusPill = ({ state = 'inactive' }) => {
  const meta = STATUS_META[state] || STATUS_META.inactive;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${meta.className}`}>
      <i className={`fas ${meta.icon}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
};

const IntegrationIcon = ({ item, large = false }) => (
  <div className={`${large ? 'h-14 w-14 text-xl' : 'h-11 w-11 text-lg'} shrink-0 rounded-xl border border-gray-200 bg-white shadow-sm flex items-center justify-center overflow-hidden`}>
    {item.logo ? <img src={item.logo} alt="" className={`${large ? 'h-9 w-9' : 'h-7 w-7'} object-contain`} /> : <i className={`${item.icon || 'fas fa-plug'} text-gray-700`} aria-hidden="true" />}
  </div>
);

const resolveMeetingState = (key, data) => {
  if (!data) return 'inactive';
  if (key === 'calendly') {
    if (!data.connected) return 'inactive';
    const c = data.connection || {};
    if (c.tokenStatus && c.tokenStatus !== 'active') return 'reconnect_required';
    if (c.webhookStatus && !['active', 'ready', 'configured'].includes(c.webhookStatus)) return 'attention';
    return 'ready';
  }
  if (key === 'microsoft_bookings') {
    if (!data.connected) return 'inactive';
    return data.bookingsPermission ? 'ready' : 'permission_required';
  }
  if (key === 'google_appointment_schedule') {
    if (!data.connected) return 'inactive';
    return data.calendarPermission ? 'ready' : 'permission_required';
  }
  if (key === 'hubspot_meetings') {
    if (!data.connected) return 'inactive';
    if (data.connection?.tokenStatus && data.connection.tokenStatus !== 'active') return 'reconnect_required';
    if (data.health && data.health.ready === false) return 'attention';
    return 'ready';
  }
  if (data.reconnectRequired || data.requiresReconnect) return 'reconnect_required';
  return data.connected ? 'ready' : 'inactive';
};

const IntegrationCard = ({ item, state, detail, management, onOpen }) => {
  const score = scoreFor(state, detail, null);
  const policy = item.providerKey ? management?.providers?.[item.providerKey] : null;
  const category = INTEGRATION_CATEGORIES[item.category] || {};
  const actionLabel = state === 'inactive' || state === 'not_connected' ? 'Set up' : state === 'reconnect_required' ? 'Reconnect' : 'Manage';
  return (
    <button type="button" onClick={() => onOpen(item.key)} className="group text-left bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:border-blue-200 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-200">
      <div className="flex items-start gap-3">
        <IntegrationIcon item={item} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold text-gray-950 leading-5">{item.name}</h3>
              <span className="inline-flex mt-1 items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                <i className={`fas ${category.icon || 'fa-plug'}`} /> {category.label || item.category}
              </span>
            </div>
            <div className="flex flex-col items-end gap-1.5"><StatusPill state={state} /><span className={`text-[11px] font-semibold ${score >= 90 ? 'text-emerald-600' : score >= 65 ? 'text-blue-600' : score >= 35 ? 'text-amber-700' : 'text-gray-500'}`}>{score}/100 health</span></div>
          </div>
          <p className="mt-3 text-sm leading-5 text-gray-600 min-h-[40px]">{item.description}</p>
        </div>
      </div>
      {policy?.mode === 'organization' && <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-200"><i className="fas fa-building-shield" />Organization managed</div>}
      {detail?.lastSyncAt && <div className="mt-2 text-xs text-gray-400">Last sync {new Date(detail.lastSyncAt).toLocaleString()}</div>}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {(item.capabilities || []).slice(0, 3).map((capability) => <span key={capability} className="rounded-md bg-gray-50 px-2 py-1 text-[11px] text-gray-600 ring-1 ring-inset ring-gray-200">{capability}</span>)}
        {(item.capabilities || []).length > 3 && <span className="rounded-md bg-gray-50 px-2 py-1 text-[11px] text-gray-500">+{item.capabilities.length - 3}</span>}
      </div>
      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
        <span className="font-medium text-blue-700 group-hover:text-blue-800">{actionLabel}</span>
        <i className="fas fa-arrow-right text-gray-300 group-hover:text-blue-500 transition-colors" />
      </div>
    </button>
  );
};

const DetailView = ({ item, state, detail, management, onManagementChanged, onBack, onRefresh, onConnect, connecting }) => {
  const Body = item.component;
  const category = INTEGRATION_CATEGORIES[item.category] || {};
  const isDisconnected = ['inactive', 'not_connected', 'reconnect_required', 'permission_required'].includes(state);
  const managementPolicy = item?.providerKey ? management?.providers?.[item.providerKey] : null;
  const canManageConnection = !(managementPolicy?.mode === 'organization' && management?.isAdmin !== true);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          <i className="fas fa-arrow-left" /> Back to integrations
        </button>
        <button onClick={onRefresh} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <i className="fas fa-rotate-right" /> Refresh status
        </button>
      </div>

      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <IntegrationIcon item={item} large />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-950">{item.name}</h2>
                <StatusPill state={state} />
              </div>
              <p className="mt-1 text-sm text-gray-500"><i className={`fas ${category.icon || 'fa-plug'} mr-1.5`} />{category.label}</p>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600">{item.description}</p>
            </div>
            {item.startEndpoint && isDisconnected && canManageConnection && (
              <button disabled={connecting} onClick={() => onConnect(item)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50">
                <i className={`fas ${connecting ? 'fa-spinner fa-spin' : 'fa-link'}`} /> {connecting ? 'Connecting…' : state === 'reconnect_required' ? 'Reconnect' : 'Connect'}
              </button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-100">
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-900">Capabilities</h3>
            <div className="mt-3 space-y-2">
              {(item.capabilities || []).map((capability) => <div key={capability} className="flex items-center gap-2 text-sm text-gray-600"><i className="fas fa-check text-emerald-500 text-xs" />{capability}</div>)}
            </div>
          </div>
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-900">Health & connection</h3>
            <div className="mt-3 text-sm text-gray-600 space-y-2">
              <div className="flex justify-between gap-4"><span>Status</span><strong className="text-gray-900">{(STATUS_META[state] || STATUS_META.inactive).label}</strong></div>
              {detail?.email && <div className="flex justify-between gap-4"><span>Account</span><strong className="text-gray-900 truncate">{detail.email}</strong></div>}
              {detail?.mappedContacts != null && <div className="flex justify-between gap-4"><span>Mapped contacts</span><strong className="text-gray-900">{detail.mappedContacts}</strong></div>}
              {detail?.configuredCampaigns != null && <div className="flex justify-between gap-4"><span>Campaigns</span><strong className="text-gray-900">{detail.configuredCampaigns}</strong></div>}
              {detail?.lastSyncAt && <div className="flex justify-between gap-4"><span>Last sync</span><strong className="text-gray-900">{new Date(detail.lastSyncAt).toLocaleString()}</strong></div>}
              {detail?.error && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-amber-800"><i className="fas fa-triangle-exclamation mr-2" />{detail.error}</div>}
            </div>
          </div>
        </div>
      </section>

      <IntegrationManagementPanel item={item} management={management} onChanged={onManagementChanged} />

      <IntegrationHealthPanel item={item} state={state} detail={detail} management={management} onConnect={onConnect} onRefresh={onRefresh} connecting={connecting} />

      {!!item.eventTypes?.length && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Events</h3>
          <p className="mt-1 text-sm text-gray-500">These business events can be used by this integration. Agent events are emitted from the same audited execution lifecycle as manual actions.</p>
          <div className="mt-3 flex flex-wrap gap-2">{item.eventTypes.map((event) => <code key={event} className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-700 ring-1 ring-slate-200">{event}</code>)}</div>
        </section>
      )}

      <IntegrationActivityPanel item={item} />

      {Body ? (
        <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm"><Body /></section>
      ) : !item.systemManaged && (
        <section className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
          <h3 className="text-sm font-semibold text-gray-900">Campaign configuration</h3>
          <p className="mt-1 text-sm text-gray-600">Provider-specific settings such as CRM mapping, meeting type or booking link are selected per campaign so Agent Mode uses the correct destination and context.</p>
          <a href="/campaigns" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800">Open campaigns <i className="fas fa-arrow-right" /></a>
        </section>
      )}
    </div>
  );
};

const IntegrationsView = ({ initialSelectedKey, onSelectKeyChange }) => {
  const [selectedKey, setSelectedKey] = useState(initialSelectedKey || null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [configMap, setConfigMap] = useState({});
  const [management, setManagement] = useState({ isAdmin: false, providers: {} });
  const [providerState, setProviderState] = useState({});
  const [providerDetail, setProviderDetail] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectingKey, setConnectingKey] = useState(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    const nextState = {};
    const nextDetail = {};
    try {
      const [configResult, managementResult] = await Promise.all([
        api.get('/integrations/config/config').catch(() => ({ data: {} })),
        api.get('/integrations/config/management').catch(() => ({ data: { isAdmin: false, providers: {} } })),
      ]);
      setConfigMap(configResult.data || {});
      setManagement(managementResult.data || { isAdmin: false, providers: {} });

      const crmResult = await api.get('/integrations/crm/provider-health').catch(() => ({ data: { providers: [] } }));
      (crmResult.data?.providers || []).forEach((provider) => {
        const state = provider.health?.operationalReady ? 'ready' : provider.health?.connected ? 'attention' : 'inactive';
        nextState[`crm:${provider.provider}`] = state;
        nextDetail[`crm:${provider.provider}`] = {
          mappedContacts: provider.mappedContacts || 0,
          lastSyncAt: provider.syncState?.lastSyncAt || provider.syncState?.updatedAt || null,
          syncState: provider.syncState || null,
          syncHealth: provider.syncHealth || null,
          connectionHealth: provider.health || null,
          error: provider.health?.issues?.[0]?.message || provider.syncHealth?.lastError?.message || provider.syncHealth?.lastError?.code || provider.syncHealth?.reason || null,
        };
      });

      const statusItems = INTEGRATIONS.filter((item) => item.statusEndpoint && !['google_email', 'microsoft_email'].includes(item.key));
      await Promise.all(statusItems.map(async (item) => {
        try {
          const response = await api.get(item.statusEndpoint);
          nextState[item.key] = resolveMeetingState(item.key, response.data);
          nextDetail[item.key] = response.data || {};
        } catch (error) {
          nextState[item.key] = 'inactive';
          nextDetail[item.key] = { error: error.response?.data?.message || null };
        }
      }));

      // Email accounts are shared by Gmail/Google Workspace and Microsoft 365.
      try {
        const emailResponse = await api.get('/email/inbox-permissions/status');
        const list = Array.isArray(emailResponse.data?.mailboxes) ? emailResponse.data.mailboxes : [];
        const google = list.find((account) => account.provider === 'google');
        const microsoft = list.find((account) => account.provider === 'microsoft');
        nextState.google_email = google ? (google.inboxReadEnabled ? 'ready' : 'permission_required') : 'inactive';
        nextDetail.google_email = google || {};
        nextState.microsoft_email = microsoft ? (microsoft.inboxReadEnabled ? 'ready' : 'permission_required') : 'inactive';
        nextDetail.microsoft_email = microsoft || {};
      } catch (_) {
        // Keep email cards visible even if mailbox status is temporarily unavailable.
      }

      setProviderState(nextState);
      setProviderDetail(nextDetail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const stateMap = useMemo(() => {
    const map = {};
    INTEGRATIONS.forEach((item) => {
      if (item.comingSoon) map[item.key] = 'coming';
      else if (item.alwaysActive) map[item.key] = 'active';
      else if (item.providerKey && providerState[`crm:${item.providerKey}`]) map[item.key] = providerState[`crm:${item.providerKey}`];
      else if (providerState[item.key]) map[item.key] = providerState[item.key];
      else {
        const cfg = configMap[item.key];
        const active = item.isActiveFromConfig ? item.isActiveFromConfig(cfg) : !!cfg;
        map[item.key] = active ? 'active' : 'inactive';
      }
    });
    return map;
  }, [configMap, providerState]);

  const detailMap = useMemo(() => {
    const result = { ...providerDetail };
    INTEGRATIONS.forEach((item) => {
      if (item.providerKey && providerDetail[`crm:${item.providerKey}`]) result[item.key] = providerDetail[`crm:${item.providerKey}`];
    });
    return result;
  }, [providerDetail]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return INTEGRATIONS.filter((item) => {
      const searchable = [item.name, item.description, ...(item.capabilities || [])].join(' ').toLowerCase();
      const state = stateMap[item.key];
      const activeGroup = ['active', 'ready', 'configured'].includes(state) ? 'active' : ['attention', 'permission_required', 'reconnect_required'].includes(state) ? 'attention' : 'inactive';
      return (!q || searchable.includes(q)) && (categoryFilter === 'all' || item.category === categoryFilter) && (statusFilter === 'all' || statusFilter === activeGroup);
    }).sort((a, b) => {
      const rank = (item) => ['attention', 'permission_required', 'reconnect_required'].includes(stateMap[item.key]) ? 0 : ['active', 'ready', 'configured'].includes(stateMap[item.key]) ? 1 : 2;
      return rank(a) - rank(b) || a.name.localeCompare(b.name);
    });
  }, [query, categoryFilter, statusFilter, stateMap]);

  const counts = useMemo(() => {
    const values = Object.values(stateMap);
    return {
      active: values.filter((state) => ['active', 'ready', 'configured'].includes(state)).length,
      attention: values.filter((state) => ['attention', 'permission_required', 'reconnect_required'].includes(state)).length,
      total: INTEGRATIONS.length,
    };
  }, [stateMap]);

  const selectedItem = selectedKey ? INTEGRATIONS.find((item) => item.key === selectedKey) : null;

  const handleOpen = (key) => { setSelectedKey(key); onSelectKeyChange?.(key); };
  const handleBack = () => { setSelectedKey(null); onSelectKeyChange?.(null); loadConfig(); };
  const handleConnect = async (item) => {
    if (!item.startEndpoint) return;
    const policy = item.providerKey ? management?.providers?.[item.providerKey] : null;
    if (policy?.mode === 'organization' && management?.isAdmin !== true) return;
    setConnectingKey(item.key);
    try {
      const { data } = await api.post(item.startEndpoint, { returnPath: `/settings?tab=integrations&integration=${item.key}` });
      if (data?.url) window.location.assign(data.url);
      else await loadConfig();
    } catch (error) {
      console.error(`Failed to connect ${item.name}`, error);
      await loadConfig();
    } finally { setConnectingKey(null); }
  };

  if (selectedItem) {
    return <DetailView item={selectedItem} state={stateMap[selectedItem.key]} detail={detailMap[selectedItem.key]} management={management} onManagementChanged={loadConfig} onBack={handleBack} onRefresh={loadConfig} onConnect={handleConnect} connecting={connectingKey === selectedItem.key} />;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-950">Integrations</h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">Connect the systems Leadgenflow uses for communication, CRM context, meetings and automation. Status is based on the real connection, permissions and sync health — not just whether configuration exists.</p>
          </div>
          <div className="grid grid-cols-3 gap-2 min-w-[300px]">
            <div className="rounded-xl bg-gray-50 p-3 text-center"><div className="text-xl font-semibold text-gray-950">{counts.total}</div><div className="text-[11px] uppercase tracking-wide text-gray-500">Available</div></div>
            <div className="rounded-xl bg-emerald-50 p-3 text-center"><div className="text-xl font-semibold text-emerald-700">{counts.active}</div><div className="text-[11px] uppercase tracking-wide text-emerald-700">Active</div></div>
            <div className="rounded-xl bg-amber-50 p-3 text-center"><div className="text-xl font-semibold text-amber-700">{counts.attention}</div><div className="text-[11px] uppercase tracking-wide text-amber-700">Attention</div></div>
          </div>
        </div>
      </div>

      {counts.attention > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div><div className="text-sm font-semibold text-amber-900"><i className="fas fa-triangle-exclamation mr-2" />{counts.attention} integration{counts.attention === 1 ? '' : 's'} need attention</div><p className="mt-1 text-sm text-amber-800">They are sorted first below so admins can resolve reconnects, permissions or stale syncs before they affect campaigns.</p></div>
          <button onClick={() => setStatusFilter('attention')} className="shrink-0 rounded-lg bg-amber-900 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-950">Review issues</button>
        </div>
      )}

      <div className="flex flex-col xl:flex-row xl:items-center gap-3">
        <div className="relative flex-1 max-w-xl">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search integrations or capabilities…" className="w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 xl:pb-0">
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700">
            <option value="all">All types</option>
            {Object.entries(INTEGRATION_CATEGORIES).map(([key, category]) => <option key={key} value={key}>{category.label}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700">
            <option value="all">All statuses</option><option value="active">Active</option><option value="attention">Needs attention</option><option value="inactive">Not connected</option>
          </select>
          <button onClick={loadConfig} disabled={loading} className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"><i className={`fas fa-rotate-right ${loading ? 'fa-spin' : ''}`} /></button>
        </div>
      </div>

      {loading && !Object.keys(stateMap).length ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">{[1,2,3,4,5,6].map((n) => <div key={n} className="h-56 animate-pulse rounded-2xl border border-gray-200 bg-white p-5"><div className="h-11 w-11 rounded-xl bg-gray-100" /><div className="mt-4 h-4 w-2/3 rounded bg-gray-100" /><div className="mt-3 h-3 w-full rounded bg-gray-100" /></div>)}</div>
      ) : filtered.length ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">{filtered.map((item) => <IntegrationCard key={item.key} item={item} state={stateMap[item.key]} detail={detailMap[item.key]} management={management} onOpen={handleOpen} />)}</div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center"><i className="fas fa-filter text-gray-300 text-2xl" /><h3 className="mt-3 font-medium text-gray-800">No integrations found</h3><p className="mt-1 text-sm text-gray-500">Try another search term or filter.</p><button onClick={() => { setQuery(''); setCategoryFilter('all'); setStatusFilter('all'); }} className="mt-3 text-sm font-medium text-blue-700">Clear filters</button></div>
      )}
    </div>
  );
};

export default IntegrationsView;
